import { buildSearchPayload } from '@civilgrid/ui/hooks';
import type { FilterGroupItem } from '@civilgrid/ui/utils';
import { type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MultiPolygon } from 'geojson';
import type {
  BulkAddProjectBookmarksBody,
  BulkInviteMembersBody,
  BulkUpdateProjectsBody,
  ProjectsSearchPayload,
} from '~/api/projectsV3';
import {
  bulkAddProjectBookmarks,
  bulkInviteMembers,
  bulkUpdateProjects,
  exportProjectsToExcel,
  fetchAllProjects,
  fetchProjects,
  fetchSandboxProject,
  updateProjectBookmark,
} from '~/api/projectsV3';
import { downloadExcelFileBuffer } from '~/common/exportUtils';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyExportError } from '~/common/notifyExportError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project } from '~/common/types';

export type UseGetProjectsParams = {
  organizationId: string;
  pageIndex?: number;
  pageSize?: number;
  sorting?: { id: string; desc: boolean }[];
  filters?: FilterGroupItem;
  globalFilter?: string;
  enabled?: boolean;
  /** When true, only return bookmarked projects (table "Bookmarks" button). Passed as API prop only, not a filter. */
  bookmarkedOnly?: boolean;
  /** When set, only return projects intersecting this map viewport geometry (Filter Table to Map View). */
  geometryFilter?: MultiPolygon | null;
};

/**
 * Prefetch projects for the given organization and search state.
 */
export function prefetchProjectsQuery(queryClient: QueryClient, params: UseGetProjectsParams) {
  const {
    organizationId,
    pageIndex = 0,
    pageSize = 25,
    sorting,
    filters,
    globalFilter,
    bookmarkedOnly = false,
    geometryFilter,
  } = params;
  const basePayload = buildSearchPayload({
    pageIndex,
    pageSize,
    sorting,
    filters,
    globalFilter,
  });
  const searchPayload = {
    ...basePayload,
    ...(bookmarkedOnly && { bookmarkedOnly: true }),
    ...(geometryFilter && { geometryFilter }),
  };

  return queryClient.prefetchQuery({
    queryKey: ['projects', { organizationId, searchPayload }],
    queryFn: ({ signal }) => fetchProjects(organizationId, searchPayload, { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch project list for the current organization with optional pagination, sorting, and filters.
 * Used by the project landing page (Table / Map / Split views).
 * Same TanStack Query defaults as drf-app requests (staleTime 5m, no refetch on window focus).
 */
export function useGetProjects(params: UseGetProjectsParams) {
  const {
    organizationId,
    pageIndex = 0,
    pageSize = 25,
    sorting,
    filters,
    globalFilter,
    enabled = true,
    bookmarkedOnly = false,
    geometryFilter,
  } = params;

  const basePayload = buildSearchPayload({
    pageIndex,
    pageSize,
    sorting,
    filters,
    globalFilter,
  });
  const searchPayload = {
    ...basePayload,
    ...(bookmarkedOnly && { bookmarkedOnly: true }),
    ...(geometryFilter && { geometryFilter }),
  };

  return useQuery({
    queryKey: ['projects', { organizationId, searchPayload }],
    queryFn: ({ signal }) => fetchProjects(organizationId, searchPayload, { signal }),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Use cache when table remounts (e.g. toggle split ↔ table)
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: (enabled ?? true) && !!organizationId,
  });
}

/**
 * Fetch the org's sample (sandbox) project. Only runs when enabled (e.g. inOnboardingMode).
 * Returns Project | null; 404 or Sandbox-plan org yields null.
 */
export function useGetSandboxProject(organizationId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['projects', 'sandbox', organizationId],
    queryFn: ({ signal }) => fetchSandboxProject(organizationId, { signal }),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!organizationId,
  });
}

/**
 * TODO: Remove this query + endpoint following Projects Tile layer work.
 * This is a temporary solutions for populating filtered map data.
 * It is NOT intended to be a scalable long-term solution.
 */
export type UseGetAllProjectsForMapParams = {
  organizationId: string;
  filters?: FilterGroupItem;
  globalFilter?: string;
  /** When true (split view + bookmarks selected), only return bookmarked projects. */
  bookmarkedOnly?: boolean;
  enabled?: boolean;
};

/**
 * Fetch all projects for the map (no pagination). Uses same filters/globalFilter as table and map list.
 * Map view always gets all projects; in split view, bookmarkedOnly is applied when bookmarks are selected.
 */
export function useGetAllProjectsForMap(params: UseGetAllProjectsForMapParams) {
  const { organizationId, filters, globalFilter, bookmarkedOnly, enabled = true } = params;

  const basePayload = buildSearchPayload({
    pageIndex: 0,
    pageSize: 1,
    sorting: undefined,
    filters,
    globalFilter,
  });
  const searchPayload = {
    ...basePayload,
    ...(bookmarkedOnly && { bookmarkedOnly: true }),
  };

  return useQuery({
    queryKey: ['projects', 'allForMap', { organizationId, searchPayload }],
    queryFn: ({ signal }) => fetchAllProjects(organizationId, searchPayload, { signal }),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!organizationId,
  });
}

/**
 * Params for infinite project list (map list). Same as useGetProjects but no sorting
 * (sort is always blank for map list). Used with useGetProjectsInfinite for infinite scroll.
 */
export type UseGetProjectsInfiniteParams = {
  organizationId: string;
  pageSize?: number;
  filters?: FilterGroupItem;
  globalFilter?: string;
  enabled?: boolean;
  /** Only return projects in the current user's bookmarks (map list "Bookmarked" section). */
  bookmarkedOnly?: boolean;
  /** Exclude projects in the current user's bookmarks (map list "My" / "All" sections). */
  excludeBookmarked?: boolean;
  /** When set, only return projects intersecting this map viewport geometry. */
  geometryFilter?: MultiPolygon | null;
};

/**
 * Infinite query for project list. Same fetch shape as paginated useGetProjects
 * (limit/skip, filters, globalFilter) but no sort. Use for map list with infinite scroll.
 * Shares filters/globalFilter with table; only sort differs (always blank here).
 */
export function useGetProjectsInfinite(params: UseGetProjectsInfiniteParams) {
  const {
    organizationId,
    pageSize = 25,
    filters,
    globalFilter,
    enabled = true,
    bookmarkedOnly,
    excludeBookmarked,
    geometryFilter,
  } = params;

  const basePayload = buildSearchPayload({
    pageIndex: 0,
    pageSize,
    sorting: undefined,
    filters,
    globalFilter,
  });
  const searchPayload = {
    ...basePayload,
    ...(bookmarkedOnly && { bookmarkedOnly: true }),
    ...(excludeBookmarked && { excludeBookmarked: true }),
    ...(geometryFilter && { geometryFilter }),
  };

  return useInfiniteQuery({
    queryKey: [
      'projects',
      'infinite',
      {
        organizationId,
        searchPayload,
        pageSize,
      },
    ],
    queryFn: ({ pageParam, signal }) => {
      const pagePayload = buildSearchPayload({
        pageIndex: pageParam as number,
        pageSize,
        sorting: undefined,
        filters,
        globalFilter,
      });
      const payload = {
        ...pagePayload,
        ...(bookmarkedOnly && { bookmarkedOnly: true }),
        ...(excludeBookmarked && { excludeBookmarked: true }),
        ...(geometryFilter && { geometryFilter }),
      };
      return fetchProjects(organizationId, payload, { signal });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * pageSize;
      return loaded < lastPage.count ? allPages.length : undefined;
    },
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: (enabled ?? true) && !!organizationId,
  });
}

/**
 * Toggle a project's bookmark (add/remove). State is read from and written to the API;
 * invalidating the projects query cache refetches lists so bookmarked is correct.
 */
export function useToggleProjectBookmark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => updateProjectBookmark(projectId),
    onSuccess: (_, _projectId) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Bulk update projects by selection (partial ids or all with exclusions).
 * Invalidates projects queries on success.
 */
export function useBulkUpdateProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkUpdateProjectsBody) => bulkUpdateProjects(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Bulk add projects to the current user's bookmarks by selection.
 * Invalidates projects queries on success.
 */
export function useBulkAddProjectBookmarks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkAddProjectBookmarksBody) => bulkAddProjectBookmarks(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Bulk invite users to projects by selection. Invalidates projects queries on success.
 */
export function useBulkInviteMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkInviteMembersBody) => bulkInviteMembers(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Export projects table to Excel (export visible = current filters; export all = no filters).
 * Same pattern as DRF requests export.
 */
type ExportProjectsTablePayload = {
  columnDefs: { id: string; header: string }[];
  search: ProjectsSearchPayload;
};

/** Marks a failure from the browser-side Blob/DOM download step, distinct from the API request itself. */
export class ClientDownloadError extends Error {}

/** Extracted so the export-failure toast can be exercised without mounting the mutation hook. */
export function reportProjectsExportFailure(error: Error, organizationId: string) {
  const reason = error instanceof ClientDownloadError ? 'client_download_error' : getReasonForError(error);
  captureTypedPosthogEvent(POSTHOG_EVENTS.exportProjectsTableFailed, {
    organization_id: organizationId,
    reason,
  });
  notifyExportError();
}

export function useExportProjectsTable(organizationId: string) {
  return useMutation<void, Error, ExportProjectsTablePayload>({
    mutationKey: ['projects-table-excel-export', { organizationId }],
    mutationFn: (payload) =>
      exportProjectsToExcel(organizationId, payload).then((buffer) => {
        try {
          downloadExcelFileBuffer(buffer as unknown as Buffer, 'projects');
        } catch (err) {
          throw new ClientDownloadError(err instanceof Error ? err.message : 'Client download failed');
        }
      }),
    onError: (error) => reportProjectsExportFailure(error, organizationId),
    onSuccess: () => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.exportProjectsTable, {
        organization_id: organizationId,
      });
    },
  });
}

export type { Project };
